import {
  BrowserRouter as Router,
  Routes as ReactRoutes,
  Route as ReactRoute,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import Signup from "./pages/Auth/Signup";
import Login from "./pages/Auth/Login";
import { PrivateRoute } from "./PrivateRoute.js";
import { PATH } from "./utils/config.js";
import Settings from "@pages/Settings";
import WheelView from "./pages/WheelView/index.jsx";
import Profile from "./pages/Profile/index.jsx";
import CreateTimedWheel from "./pages/Wheels/CreateTimedWheel/index.jsx";
import PrivacyPolicy from "./pages/PrivacyPolicy/index.js";
import Terms from "./pages/Terms/index.js";
import OthersProfile from "./pages/OthersProfile/index.jsx";
import Invite from "./pages/Invite";
import SegmentBuilder from "./pages/SegmentBuilder";
import ViewWheel from "./pages/ViewWheel/index.jsx";
import Gallery from "./pages/Gallery/index.jsx";
import ForgotPassword from "./pages/Auth/ForgotPassword/index.jsx";
import Analytics from "./pages/Referrals/index.jsx";
import { getLoggedInUser, getToken, isUserLoggedIn } from "./utils/helpers.js";
import Notifications from "./pages/Notifications/index.jsx";
import LinkVaultPage from "./pages/LinkVaultPage/index.jsx";
import ExplorePage from "./pages/Explore/index.jsx";
import Onboarding from "./pages/Onboarding/index.jsx";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { getUserProfileByIdAction } from "./redux/actions/user.js";
import { getActiveSubscription } from "./redux/actions/subscription.js";
import NotFound from "./pages/NotFound/index.jsx";
import Segment from "./pages/Segment/index.jsx";

const GalleryRoute = () => {
  const location = useLocation();
  const { id } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const isLinkVault = searchParams.get("view") === "linkvault";
  const isAuthenticated = isUserLoggedIn(); // Using token-based authentication check
  const userId = getLoggedInUser()?.id;

  // Redirect if no ID is provided but user is authenticated
  if (!id && isAuthenticated && userId) {
    return <Navigate to={`/gallery/${userId}`} replace />;
  }

  // Redirect to login if no ID and not authenticated
  if (!id && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Show LinkVault preview only for non-authenticated users when view=linkvault
  if (isLinkVault && !isAuthenticated) {
    return <LinkVaultPage />;
  }

  // If there's an ID in the URL, allow access regardless of authentication status
  if (id) {
    return <Gallery />;
  }

  // For all other cases (authenticated users or non-linkvault views)
  return (
    <PrivateRoute>
      <Gallery />
    </PrivateRoute>
  );
};

// Auth routes redirect if user is already logged in
const AuthRoute = ({ children }) => {
  const isAuthenticated = isUserLoggedIn(); // Using token-based authentication check
  const userId = getLoggedInUser()?.id;

  if (isAuthenticated && userId) {
    return <Navigate to={`/gallery/${userId}`} replace />;
  }

  return children;
};

const Routes = () => {
  const userId = getLoggedInUser()?.id;
  const isAuthenticated = isUserLoggedIn();

  const dispatch = useDispatch();

  useEffect(() => {
    if (isAuthenticated && userId) {
      dispatch(getUserProfileByIdAction(userId));
      dispatch(getActiveSubscription());
    }
  }, [userId, isAuthenticated, dispatch]);

  return (
    <>
      {/* <Navbar /> */}

      <ReactRoutes>
        <ReactRoute
          path={PATH.DASHBOARD}
          element={
            isAuthenticated && userId ? (
              <Navigate to={PATH.GALLERY + "/" + userId} replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <ReactRoute
          exact
          path={PATH.CREATE_WHEEL}
          element={
            <PrivateRoute>
              <CreateTimedWheel></CreateTimedWheel>
            </PrivateRoute>
          }
        />

        <ReactRoute
          exact
          path="/notifications"
          element={
            <PrivateRoute>
              <Notifications />
            </PrivateRoute>
          }
        />

        <ReactRoute exact path="/explore" element={<ExplorePage />} />

        <ReactRoute exact path={PATH.VIEW_WHEEL} element={<WheelView />} />

        <ReactRoute exact path={PATH.MY_PROFILE} element={<Profile />} />

        <ReactRoute exact path={"/wheel/:wheelId"} element={<ViewWheel />} />

        <ReactRoute exact path={"/wheel/segment/:id"} element={<Segment />} />

        <ReactRoute exact path={PATH.PROFILE} element={<Profile />} />

        <ReactRoute
          exact
          path={"/privacy-policy"}
          element={<PrivacyPolicy />}
        />

        <ReactRoute exact path={"/t&c"} element={<Terms />} />
        <ReactRoute
          exact
          path={PATH.OTHER_USER_PROFILE}
          element={<OthersProfile token={false} />}
        />

        <ReactRoute
          exact
          path={PATH.LOGIN}
          element={
            <AuthRoute>
              <Login />
            </AuthRoute>
          }
        />
        <ReactRoute
          exact
          path={PATH.SIGNUP}
          element={
            <AuthRoute>
              <Signup />
            </AuthRoute>
          }
        />
        <ReactRoute
          exact
          path={`${PATH.SIGNUP}/:referralCode`}
          element={
            <AuthRoute>
              <Signup />
            </AuthRoute>
          }
        />
        <ReactRoute
          exact
          path={PATH.FORGOT_PASSWORD}
          element={
            <AuthRoute>
              <ForgotPassword />
            </AuthRoute>
          }
        />

        <ReactRoute exact path="/privacypolicy" element={<PrivacyPolicy />} />
        <ReactRoute
          exact
          path="/referrals/:status"
          element={
            <PrivateRoute>
              <Analytics />
            </PrivateRoute>
          }
        />
        <ReactRoute
          exact
          path="/referrals"
          element={
            <PrivateRoute>
              <Analytics />
            </PrivateRoute>
          }
        />

        <ReactRoute
          exact
          path="/invite"
          element={
            <PrivateRoute>
              <Invite />
            </PrivateRoute>
          }
        />

        <ReactRoute exact path="/profile" element={<Profile />} />
        <ReactRoute exact path="/wheelview/:wheelId" element={<WheelView />} />
        <ReactRoute
          exact
          path="/wheelview/:wheelId/:wheelName"
          element={<WheelView />}
        />

        <ReactRoute
          exact
          path="/settings"
          element={
            <PrivateRoute>
              <Settings />
            </PrivateRoute>
          }
        />

        <ReactRoute exact path={PATH.GALLERY} element={<GalleryRoute />} />

        <ReactRoute exact path={PATH.USER_GALLERY} element={<GalleryRoute />} />

        <ReactRoute exact path={PATH.ONBOARDING} element={<Onboarding />} />

        <ReactRoute
          exact
          path="/builder/:id?"
          element={
            <PrivateRoute>
              <SegmentBuilder />
            </PrivateRoute>
          }
        />

        {/* Catch-all route for 404 - must be last */}
        <ReactRoute path="*" element={<NotFound />} />
      </ReactRoutes>
    </>
  );
};

export default Routes;
